import { useContext, useEffect, useState } from 'react';
import { FaTimes, FaPlay, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { getProblemUrl } from '../../utils/problemUtils';
import api from '../../utils/api';

const ProblemPreviewModal = ({ problem, onClose }) => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const [problemDetails, setProblemDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProblemDetails = async () => {
      try {
        setLoading(true);
        const problemId = problem.slug || problem._id;
        const response = await api.get(`/api/problems/${problemId}/essential`);
        setProblemDetails(response.data);
      } catch (error) {
        console.error('Failed to fetch problem details:', error);
        setProblemDetails(null);
      } finally {
        setLoading(false);
      }
    };

    if (problem) {
      fetchProblemDetails();
    }
  }, [problem]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleStartProblem = () => {
    const url = getProblemUrl(problem);
    navigate(url);
    onClose();
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy':
        return 'text-green-600 dark:text-green-400';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'hard':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] rounded-lg shadow-2xl overflow-hidden animate-slide-up"
        style={{ backgroundColor: theme.background.primary }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 flex items-center justify-between p-4 border-b"
          style={{ 
            backgroundColor: theme.background.secondary,
            borderColor: theme.border.primary 
          }}
        >
          <div className="flex-1">
            <h2 
              className="text-xl font-bold"
              style={{ color: theme.text.primary }}
            >
              {problem?.title || 'Problem Preview'}
            </h2>
            {problem?.difficulty && (
              <span className={`text-sm font-medium ${getDifficultyColor(problem.difficulty)}`}>
                {problem.difficulty}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-opacity-10 transition-colors"
            style={{ 
              color: theme.text.secondary,
              backgroundColor: 'transparent'
            }}
            title="Close (Esc)"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-4xl" style={{ color: theme.text.accent }} />
            </div>
          ) : problemDetails ? (
            <div className="space-y-6">
              {/* Description */}
              {problemDetails.description && (
                <div>
                  <h3 
                    className="text-lg font-semibold mb-3"
                    style={{ color: theme.text.primary }}
                  >
                    Description
                  </h3>
                  <div 
                    className="prose prose-sm max-w-none dark:prose-invert"
                    style={{ color: theme.text.secondary }}
                    dangerouslySetInnerHTML={{ __html: problemDetails.description }}
                  />
                </div>
              )}

              {/* Examples */}
              {problemDetails.examples && problemDetails.examples.length > 0 && (
                <div>
                  <h3 
                    className="text-lg font-semibold mb-3"
                    style={{ color: theme.text.primary }}
                  >
                    Examples
                  </h3>
                  <div className="space-y-4">
                    {problemDetails.examples.map((example, index) => (
                      <div 
                        key={index}
                        className="p-4 rounded-lg"
                        style={{ backgroundColor: theme.background.secondary }}
                      >
                        <div className="font-semibold mb-2" style={{ color: theme.text.primary }}>
                          Example {index + 1}:
                        </div>
                        {example.input && (
                          <div className="mb-2">
                            <span className="font-medium" style={{ color: theme.text.secondary }}>Input: </span>
                            <code 
                              className="px-2 py-1 rounded text-sm"
                              style={{ 
                                backgroundColor: theme.background.tertiary,
                                color: theme.text.accent 
                              }}
                            >
                              {example.input}
                            </code>
                          </div>
                        )}
                        {example.output && (
                          <div className="mb-2">
                            <span className="font-medium" style={{ color: theme.text.secondary }}>Output: </span>
                            <code 
                              className="px-2 py-1 rounded text-sm"
                              style={{ 
                                backgroundColor: theme.background.tertiary,
                                color: theme.text.accent 
                              }}
                            >
                              {example.output}
                            </code>
                          </div>
                        )}
                        {example.explanation && (
                          <div>
                            <span className="font-medium" style={{ color: theme.text.secondary }}>Explanation: </span>
                            <span style={{ color: theme.text.tertiary }}>{example.explanation}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Constraints */}
              {problemDetails.constraints && problemDetails.constraints.length > 0 && (
                <div>
                  <h3 
                    className="text-lg font-semibold mb-3"
                    style={{ color: theme.text.primary }}
                  >
                    Constraints
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    {problemDetails.constraints.map((constraint, index) => {
                      // Handle both string and object constraints
                      if (typeof constraint === 'string') {
                        return (
                          <li 
                            key={index}
                            style={{ color: theme.text.secondary }}
                          >
                            {constraint}
                          </li>
                        );
                      }
                      
                      // Handle object constraints with parameter, min, max
                      const { parameter, min, max, description } = constraint;
                      const constraintText = parameter 
                        ? `${min?.toLocaleString()} ≤ ${parameter} ≤ ${max?.toLocaleString()}${description ? ` (${description})` : ''}`
                        : description || JSON.stringify(constraint);
                      
                      return (
                        <li 
                          key={index}
                          style={{ color: theme.text.secondary }}
                        >
                          <code 
                            className="px-1.5 py-0.5 rounded text-xs mr-1"
                            style={{ 
                              backgroundColor: theme.background.tertiary,
                              color: theme.text.accent 
                            }}
                          >
                            {constraintText}
                          </code>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Guarantees */}
              {problemDetails.guarantees && problemDetails.guarantees.length > 0 && (
                <div>
                  <h3 
                    className="text-lg font-semibold mb-3"
                    style={{ color: theme.text.primary }}
                  >
                    Guarantees
                  </h3>
                  <ul className="list-disc list-inside space-y-1">
                    {problemDetails.guarantees.map((guarantee, index) => (
                      <li 
                        key={index}
                        style={{ color: theme.text.secondary }}
                      >
                        {guarantee}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tags */}
              {problemDetails.tags && problemDetails.tags.length > 0 && (
                <div>
                  <h3 
                    className="text-lg font-semibold mb-3"
                    style={{ color: theme.text.primary }}
                  >
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {problemDetails.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-sm"
                        style={{ 
                          backgroundColor: theme.background.tertiary,
                          color: theme.text.accent 
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p style={{ color: theme.text.secondary }}>Failed to load problem details</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div 
          className="sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t"
          style={{ 
            backgroundColor: theme.background.secondary,
            borderColor: theme.border.primary 
          }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
            style={{ 
              backgroundColor: theme.background.tertiary,
              color: theme.text.secondary 
            }}
          >
            Close
          </button>
          <button
            onClick={handleStartProblem}
            className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors hover:opacity-90"
            style={{ 
              backgroundColor: theme.button.primary.bg,
              color: theme.button.primary.text 
            }}
          >
            <FaPlay size={14} />
            Start Problem
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProblemPreviewModal;
